"use client";

import { Trans } from "@lingui/react/macro";
import {
  CheckIcon,
  Columns3Icon,
  CopyIcon,
  DownloadIcon,
  EyeIcon,
  ListOrderedIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export type VizExportFormat = "csv" | "json" | "png";
export type VizSortDirection = "asc" | "desc" | "none";
type VizCheckboxMenuEntry = {
  key: string;
  label: string;
};

/**
 * AI 可視化カードの操作群を、本文から分離して右上へ寄せる。
 *
 * @remarks
 * チャート直前の視線移動を減らすため、余白は狭すぎず広すぎない固定値へ揃える。
 */
export function VizActionBar({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-2 flex flex-wrap items-center justify-end gap-1.5 px-0.5">
      {children}
    </div>
  );
}

export function VizSortMenu(args: {
  value: VizSortDirection;
  onChange: (value: VizSortDirection) => void;
  includeNone?: boolean;
}) {
  const { value, onChange, includeNone = true } = args;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" size="xs">
          <ListOrderedIcon className="size-3.5" />
          <Trans>Sort</Trans>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>
          <Trans>Sort</Trans>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup
          value={value}
          onValueChange={(nextValue) => {
            if (
              nextValue === "asc" ||
              nextValue === "desc" ||
              nextValue === "none"
            ) {
              onChange(nextValue);
            }
          }}
        >
          <DropdownMenuRadioItem value="desc">
            <Trans>Descending</Trans>
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="asc">
            <Trans>Ascending</Trans>
          </DropdownMenuRadioItem>
          {includeNone ? (
            <DropdownMenuRadioItem value="none">
              <Trans>Original order</Trans>
            </DropdownMenuRadioItem>
          ) : null}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * 可視列・可視指標のような multi-select menu を共通化する。
 *
 * @remarks
 * checkbox item を 1 回押すたびに menu が閉じると、複数項目を連続で切り替える操作が
 * かなり重くなる。`onSelect.preventDefault()` で open state を維持しつつ、
 * 外側 click / Escape では通常どおり閉じられる挙動にそろえる。
 */
function VizCheckboxMenu(args: {
  icon: ReactNode;
  triggerLabel: ReactNode;
  contentLabel: ReactNode;
  entries: VizCheckboxMenuEntry[];
  visibleKeySet: ReadonlySet<string>;
  onToggle: (key: string, checked: boolean) => void;
}) {
  const { icon, triggerLabel, contentLabel, entries, visibleKeySet, onToggle } =
    args;

  if (entries.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" size="xs">
          {icon}
          {triggerLabel}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{contentLabel}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {entries.map((entry) => (
          <DropdownMenuCheckboxItem
            key={entry.key}
            checked={visibleKeySet.has(entry.key)}
            onSelect={(event) => {
              event.preventDefault();
            }}
            onCheckedChange={(checked) => {
              onToggle(entry.key, Boolean(checked));
            }}
          >
            {entry.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * 系列の表示/非表示を複数まとめて切り替える menu。
 *
 * @remarks
 * 可視指標は比較しながら何個か連続で消し込みたいことが多いので、
 * checkbox を選ぶたびに menu が閉じない interaction をここで固定する。
 */
export function VizSeriesMenu(args: {
  series: Array<{ key: string; label: string }>;
  visibleKeySet: Set<string>;
  onToggle: (key: string, checked: boolean) => void;
}) {
  const { series, visibleKeySet, onToggle } = args;

  if (series.length <= 1) return null;

  return (
    <VizCheckboxMenu
      icon={<EyeIcon className="size-3.5" />}
      triggerLabel={<Trans>Metrics</Trans>}
      contentLabel={<Trans>Visible metrics</Trans>}
      entries={series}
      visibleKeySet={visibleKeySet}
      onToggle={onToggle}
    />
  );
}

/**
 * テーブル列の表示切り替え menu。
 *
 * @remarks
 * 列の比較では複数 checkbox をまとめて切り替えることが多いため、
 * 指標 menu と同じく 1 回の選択で close しない multi-select UX を共有する。
 */
export function VizColumnsMenu(args: {
  columns: Array<{ key: string; label: string }>;
  visibleKeySet: Set<string>;
  onToggle: (key: string, checked: boolean) => void;
}) {
  const { columns, visibleKeySet, onToggle } = args;

  return (
    <VizCheckboxMenu
      icon={<Columns3Icon className="size-3.5" />}
      triggerLabel={<Trans>Columns</Trans>}
      contentLabel={<Trans>Visible columns</Trans>}
      entries={columns}
      visibleKeySet={visibleKeySet}
      onToggle={onToggle}
    />
  );
}

export function VizExportMenu(args: {
  formats: VizExportFormat[];
  onExport: (format: VizExportFormat) => void | Promise<void>;
  onCopyImage?: (() => void | Promise<void>) | null;
  disabled?: boolean;
}) {
  const { formats, onExport, onCopyImage, disabled = false } = args;
  const uniqueFormats = [...new Set(formats)].filter(
    (format): format is VizExportFormat =>
      format === "csv" || format === "json" || format === "png",
  );
  const canCopyImage =
    typeof onCopyImage === "function" && uniqueFormats.includes("png");

  if (uniqueFormats.length === 0 && !canCopyImage) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" size="xs" disabled={disabled}>
          <DownloadIcon className="size-3.5" />
          <Trans>Export</Trans>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>
          <Trans>Export</Trans>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {canCopyImage ? (
          <>
            <DropdownMenuItem
              disabled={disabled}
              onSelect={() => {
                void onCopyImage?.();
              }}
            >
              <CopyIcon className="size-3.5" />
              <Trans>Copy image</Trans>
            </DropdownMenuItem>
            {uniqueFormats.length > 0 ? <DropdownMenuSeparator /> : null}
          </>
        ) : null}
        {uniqueFormats.map((format) => (
          <DropdownMenuItem
            key={format}
            disabled={disabled}
            onSelect={() => {
              void onExport(format);
            }}
          >
            <CheckIcon className="size-3.5 opacity-0" />
            {format.toUpperCase()}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
